import { useEffect, useContext } from 'react';
import { useLocation } from '@reach/router';

import { SitePathsContext } from '../contexts/sitePaths';
import { useUseStaticQuery } from './useUseStaticQuery';
import { useParams } from './useParams';
import { useLanguage } from './useLanguage';

export const usePaths = () => {
  const location = useLocation();
  const { hash: currentPathHash } = location;
  const {
    setSitePaths,
    sitePaths: globalSitePaths,
    allCurrentPaths,
    setAllCurrentPaths,
    currentPath,
    setCurrentPath,
    otherLanguagesCurrentPath,
    setOtherLanguagesCurrentPaths,
    pageContentfulId,
    setPageContentfulId
  } = useContext(SitePathsContext);

  const { sitePaths, authorsPaths, articlesPaths } = useUseStaticQuery();
  const { langKey } = useLanguage();

  const { param: email } = useParams('email', false);
  const preferencesSitePaths = [
    {
      id: 'preference-page-id-1',
      node_locale: 'en',
      gatsbyPath: `/preferences?email=${email}`,
      contentful_id: 'preferences'
    },
    {
      id: 'preference-page-id-2',
      node_locale: 'en-US',
      gatsbyPath: `/preferences?email=${email}`,
      contentful_id: 'preferences'
    },
    {
      id: 'preference-page-id-3',
      node_locale: 'en-GB',
      gatsbyPath: `/preferences?email=${email}`,
      contentful_id: 'preferences'
    },
    {
      id: 'preference-page-id-4',
      node_locale: 'fr',
      gatsbyPath: `/fr/preferences?email=${email}`,
      contentful_id: 'preferences'
    },
    {
      id: 'preference-page-id-5',
      node_locale: 'it',
      gatsbyPath: `/it/preferenze?email=${email}`,
      contentful_id: 'preferences'
    }
  ];
  const getCurrentPathWithLanguageKey = (languageKey) => {
    const currentPath = allCurrentPaths?.find(
      (p) => p.node_locale === languageKey
    );
    return currentPath;
  };

  const getOtherLanguagesCurrentPathsWithLanguageKey = (languageKey) => {
    const paths = allCurrentPaths?.filter((p) => p.node_locale !== languageKey);
    return paths;
  };

  const getHrefWithLanguageKey = (languageKey) => {
    const origin = location?.origin;
    const path = getCurrentPathWithLanguageKey(languageKey)?.gatsbyPath;
    return `${origin}${path}`;
  };

  const getPathsWithContentfulId = (contentful_id) =>
    globalSitePaths?.filter((p) => p.contentful_id === contentful_id);

  const getPathObjectWithPathname = (pathname) => {
    const path = globalSitePaths?.find((p) => p.gatsbyPath === pathname);
    return path;
  };

  const getHomepagePath = () =>
    globalSitePaths?.find(
      (p) => p.name === 'home' && p.node_locale === langKey
    );

  const getPathWithLanguageKey = (paths) => {
    return paths?.find((p) => p.node_locale === langKey);
  };

  useEffect(() => {
    setSitePaths([
      ...sitePaths,
      ...authorsPaths,
      ...articlesPaths,
      ...preferencesSitePaths
    ]);
  }, []);

  useEffect(() => {
    pageContentfulId
      ? setAllCurrentPaths(getPathsWithContentfulId(pageContentfulId))
      : null;
  }, [globalSitePaths]);

  useEffect(() => {
    setCurrentPath(getCurrentPathWithLanguageKey(langKey));
    setOtherLanguagesCurrentPaths(
      getOtherLanguagesCurrentPathsWithLanguageKey(langKey)
    );
  }, [allCurrentPaths, langKey]);

  return {
    getCurrentPathWithLanguageKey,
    getHrefWithLanguageKey,
    getPathsWithContentfulId,
    getHomepagePath,
    getPathObjectWithPathname,
    getPathWithLanguageKey,
    allCurrentPaths,
    otherLanguagesCurrentPath,
    currentPath,
    currentPathHash,
    setPageContentfulId
  };
};
